import i18n, { Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import _upperFirst from 'lodash/upperFirst';
import _isDate from 'lodash/isDate';
import _isObject from 'lodash/isObject';
import _isString from 'lodash/isString';
import moment from 'moment-timezone';

import en from './en';
import fr from './fr';

type Dict = Record<string, unknown>;
type ResourcesByLang = Record<string, Record<string, Dict>>;

type BackendCallback = (error: Error | null, data: Dict) => void;

// --- CONFIGURATION ---
const PROJECT_TOKEN = process.env.REACT_APP_SIMPLELOCALIZE_PROJECT_TOKEN || '';
const ENVIRONMENT = process.env.REACT_APP_SIMPLELOCALIZE_ENVIRONMENT || process.env.SIMPLELOCALIZE_ENVIRONMENT || '_latest';
const FALLBACK_LNG = 'fr';
const IS_TEST = process.env.NODE_ENV === 'test' || !!process.env.JEST_WORKER_ID;

const IS_LATEST = ENVIRONMENT === '_latest';

const LOCAL_RESOURCES: ResourcesByLang = {
  en: (en as unknown) as Record<string, Dict>,
  fr: (fr as unknown) as Record<string, Dict>,
};

const NAMESPACES = Object.keys((en as unknown) as Record<string, unknown>);

// --- UTILS ---
function isObj(value: unknown): value is Dict {
  return value !== null && _isObject(value) && !Array.isArray(value);
}

/**
 * Deep merges two objects. Override takes precedence.
 */
function deepMerge(base: Dict, override: Dict): Dict {
  const result: Dict = { ...base };
  Object.keys(override).forEach((key) => {
    const baseVal = base[key];
    const overVal = override[key];

    if (isObj(baseVal) && isObj(overVal)) {
      result[key] = deepMerge(baseVal, overVal);
    } else {
      result[key] = overVal;
    }
  });

  return result;
}

function safeNamespace(resources: ResourcesByLang, lng: string, ns: string): Dict {
  const langMap = resources[lng];
  if (!langMap) return {};
  const value = langMap[ns];
  return value && isObj(value) ? value : {};
}

// --- BACKEND ---
const SimpleLocalizeBackend: {
  type: 'backend';
  init: () => void;
  read: (language: string, namespace: string, callback: BackendCallback) => void;
} = {
  type: 'backend',
  init: () => {},
  read: (language: string, namespace: string, callback: BackendCallback): void => {
    const lng = language || FALLBACK_LNG;
    const local = safeNamespace(LOCAL_RESOURCES, lng, namespace);

    if (IS_TEST || !PROJECT_TOKEN) {
      callback(null, local);
      return;
    }

    let url = `https://cdn.simplelocalize.io/${PROJECT_TOKEN}/${ENVIRONMENT}/${encodeURIComponent(lng)}/${encodeURIComponent(
      namespace,
    )}`;
    if (IS_LATEST) url += `?v=${Date.now()}`;

    fetch(url, IS_LATEST ? ({ cache: 'no-store' } as RequestInit) : undefined)
      .then((res) => (res.ok ? (res.json() as Promise<unknown>) : Promise.reject(new Error('Fetch failed'))))
      .then((remoteUnknown) => {
        const remote = isObj(remoteUnknown) ? remoteUnknown : {};
        callback(null, deepMerge(local, remote));
      })
      .catch(() => callback(null, local));
  },
};

// --- INITIALIZATION ---
if (!IS_TEST) {
  i18n.use(SimpleLocalizeBackend as any).use(LanguageDetector);
}

// eslint-disable-next-line @typescript-eslint/no-floating-promises
i18n.use(initReactI18next).init({
  debug: false,
  lng: IS_TEST ? 'en' : undefined,
  // In runtime we must NOT preload full resources, otherwise i18next won't call the backend.
  // In tests we preload local resources to keep snapshots deterministic.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  resources: (IS_TEST ? ((LOCAL_RESOURCES as unknown) as Resource) : undefined) as any,
  supportedLngs: ['fr', 'en'],
  fallbackLng: FALLBACK_LNG,
  ns: NAMESPACES,
  defaultNS: 'common',
  partialBundledLanguages: !IS_TEST,
  react: { useSuspense: false },
  detection: {
    order: ['querystring', 'localStorage', 'sessionStorage', 'navigator', 'cookie', 'path', 'subdomain', 'htmlTag'],
    lookupSessionStorage: 'language',
    caches: ['sessionStorage'],
  },
  interpolation: {
    escapeValue: false,
    format: (value: unknown, format?: string): string => {
      // i18next FormatFunction must return a string
      if (!format) return String(value);

      const parts = format.split('---');
      let acc: unknown = value;

      parts.forEach((f) => {
        if (f === 'upperFirst') {
          acc = _upperFirst(String(acc));
        } else if (_isDate(acc) || (_isString(acc) && moment(acc).isValid())) {
          // moment accepts many inputs; keep it consistent with prior behavior
          acc = moment(acc as any).format(f);
        }
      });

      return String(acc);
    },
  },
});

export default i18n;
